import React from 'react'
import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { AlertTriangleIcon } from 'lucide-react'
import { Link, useRouteError } from 'react-router'

import { Alert, AlertDescription, AlertTitle } from './ui/alert'
import { Button } from './ui/button'

import { IS_DEV } from '@/env'

export function ErrorComponent({ error: propError }: { error?: Error } = {}) {
  const routeError = useRouteError() as Error | undefined
  const queryClientErrorBoundary = useQueryErrorResetBoundary()

  // Use the route error if available, otherwise fall back to the prop error
  const error = routeError || propError

  React.useEffect(() => {
    queryClientErrorBoundary.reset()
  }, [queryClientErrorBoundary])

  // Log errors to console in development mode
  React.useEffect(() => {
    if (IS_DEV && error) {
      console.error('React Router Error Boundary caught an error:', error)
      console.error('Error message:', error.message)
      console.error('Error stack:', error.stack)
      // Also log the original error object for full inspection
      console.error('Full error object:', error)
    }
  }, [error])

  return (
    <div className="flex w-full items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Alert variant="warning">
          <AlertTriangleIcon className="size-4" />
          <AlertTitle>Hmm, quelque chose s'est mal passé</AlertTitle>
          <AlertDescription>Nous avons rencontré une erreur. Veuillez réessayer plus tard.</AlertDescription>
        </Alert>

        <div className="mt-8 space-y-4">
          <Button className="w-full" variant="secondary" onClick={() => window.location.reload()}>
            Réessayer
          </Button>
          <Button className="w-full" variant="outline">
            <Link to="/">Retour à l'accueil</Link>
          </Button>
          {IS_DEV && error && (
            <div className="mt-4 p-4 bg-red-100 text-red-800 rounded-md">
              <div className="text-xs break-all whitespace-pre-wrap font-mono max-w-full">
                <strong>Message:</strong> {error.message}
              </div>
              <div className="text-xs break-all whitespace-pre-wrap font-mono mt-2 max-w-full">
                <strong>Stack:</strong> {error.stack}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
